import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SupabaseService } from '../services/supabaseService';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { War, BattleSettings, Profile } from '../types';
import { Shield, Copy, Check, Sword, Wifi, Swords } from 'lucide-react';
import { ProfileDossier } from '../components/ProfileDossier';

const Lobby: React.FC = () => {
    const { warId } = useParams<{ warId: string }>();
    const navigate = useNavigate();
    const { error } = useAlert();
    const { user, loading } = useAuth();
    const effectiveUser = user;

    const [war, setWar] = useState<War | null>(null);
    const [myProfile, setMyProfile] = useState<Profile | null>(null);
    const [opponentProfile, setOpponentProfile] = useState<Profile | null>(null);
    const [isJoiner, setIsJoiner] = useState(false);

    // AI / Modal States
    const [isAIModalOpen, setIsAIModalOpen] = useState(false);
    const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
    const [selectedAIDifficulty, setSelectedAIDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');

    // UI States
    const [copied, setCopied] = useState(false);
    const [newName, setNewName] = useState("");
    const [isRenaming, setIsRenaming] = useState(false);
    const [presenceCount, setPresenceCount] = useState(0);


    // Profile Dossier
    const [profileDossierTarget, setProfileDossierTarget] = useState<Profile | null>(null);

    // Derived States from War Settings
    const settings = (war?.settings || {}) as BattleSettings;
    const isHost = war && effectiveUser ? war.player_id === effectiveUser.id : false;

    // Updated Readiness Logic (Host -> settings, Guest -> opponent_status)
    const hostIsReady = !!settings.host_ready;
    const guestIsReady = war?.opponent_status === 'ready';

    const iAmReady = isHost ? hostIsReady : guestIsReady;
    const opponentReady = isHost ? guestIsReady : hostIsReady;
    const opponentId = isHost ? war?.opponent_id : war?.player_id;
    const bothReady = hostIsReady && guestIsReady;
    const opponentUplinkDetected = !!opponentId || presenceCount > 1;

    // Logic for Button Visibility
    // Show if: (Opponent is present AND Both Ready) OR (No Opponent AND Host Ready) OR (AI Mode AND Host Ready)
    const canDeploy = (opponentId && bothReady) || (!opponentId && isHost && iAmReady) || (settings.opponent_type === 'ai' && iAmReady);

    const needsDrafting = React.useMemo(() => {
        if (!war) return false;
        if (isHost) return false;
        const inv = war.enemy_inventory;
        const count = inv ? Object.values(inv).reduce((a: number, b: number) => a + b, 0) : 0;
        return count === 0;
    }, [war, isHost]);

    const getOpponentId = (currentWar: War | null, currentUserId?: string | null) => {
        if (!currentWar || !currentUserId) return undefined;
        return currentWar.player_id === currentUserId
            ? currentWar.opponent_id || undefined
            : currentWar.player_id || undefined;
    };

    // Refs for Realtime Callbacks to access latest state without re-subscribing
    const opponentIdRef = React.useRef<string | undefined>(undefined);
    const currentUserIdRef = React.useRef<string | undefined>(undefined);

    // Sync refs for realtime use
    useEffect(() => {
        currentUserIdRef.current = effectiveUser?.id;
    }, [effectiveUser?.id]);

    useEffect(() => {
        opponentIdRef.current = getOpponentId(war, effectiveUser?.id);
    }, [war, effectiveUser?.id]);

    // Load Initial Data
    useEffect(() => {
        const initLobby = async () => {
            console.log("Lobby: initLobby sequence start");
            if (!warId || loading) return;

            const currentUser = effectiveUser;
            if (!currentUser) {
                navigate('/', { state: { returnUrl: `/lobby/${warId}`, action: 'join' } });
                return;
            }

            // 1. Get War
            const foundWar = await SupabaseService.getWarById(warId);
            if (!foundWar) {
                error("Campaign not found!");
                navigate('/campaigns');
                return;
            }
            setWar(foundWar);
            opponentIdRef.current = getOpponentId(foundWar, currentUser.id); // Set immediately for race conditions

            // 2. Identify Role
            const amGuest = foundWar.player_id !== currentUser.id;
            setIsJoiner(amGuest);

            // 3. Auto-Join or Check Guest Status
            if (amGuest) {
                let currentWarState = foundWar;

                const iAmOpponent = foundWar.opponent_id === currentUser.id;
                const isOccupied = !!foundWar.opponent_id;
                const canOverwrite = (foundWar.battles_fought || 0) === 0;

                // Attempt to join if not already the opponent AND (slot is free OR can overwrite)
                if (!iAmOpponent && (!isOccupied || canOverwrite)) {
                    console.log("Lobby: Attempting to join as guest...");
                    try {
                        await SupabaseService.joinLobby(warId);
                        const updated = await SupabaseService.getWarById(warId);
                        if (updated) currentWarState = updated;
                    } catch (err: any) {
                        console.error("Lobby Join Error:", err);
                        error(err.message);
                    }
                }

                if (currentWarState !== foundWar) setWar(currentWarState);
            }

            // 4. Fetch Profiles
            const myProf = await SupabaseService.getProfile();
            setMyProfile(myProf);

            // Auto-Redirect to Barracks if Army is empty
            const totalUnits = Object.values(myProf.inventory).reduce((acc, curr) => acc + (curr as number), 0);
            if (totalUnits === 0) {
                console.log("Lobby: Empty Army Detected. Redirecting to Barracks...");
                navigate('/barracks', { state: { returnUrl: `/lobby/${warId}` } });
                return;
            }

            const defaultName = currentUser?.email?.split('@')[0];
            if (myProf.username === defaultName || myProf.username === 'Commander' || myProf.username === 'Guest Commander' || myProf.username.startsWith("Commander-")) {
                setIsRenaming(true);
            }

            const opponentIdForProfile = getOpponentId(foundWar, currentUser.id);
            if (opponentIdForProfile) {
                const oppProf = await SupabaseService.getPublicProfile(opponentIdForProfile);
                setOpponentProfile(oppProf);
            }
        };

        initLobby();
    }, [warId, effectiveUser, loading, navigate]);

    // Realtime Updates
    useEffect(() => {
        if (!warId || !user) return;

        const channel = supabase
            .channel(`war_room_${warId}`, {
                config: { presence: { key: user.id } }
            })
            .on(
                'presence',
                { event: 'sync' },
                () => {
                    const state = channel.presenceState();
                    const count = Object.keys(state).length;
                    setPresenceCount(count);
                    console.log("Lobby: Presence Sync", count);
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'wars', filter: `id=eq.${warId}` },
                async (payload: any) => {
                    console.log("Lobby: [RT] War Update", payload.new);
                    const newWar = payload.new as War;
                    setWar(newWar);

                    // If Opponent Joined/Changed, fetch profile
                    const currentUserId = currentUserIdRef.current;
                    const nextOpponentId = getOpponentId(newWar, currentUserId);
                    if (nextOpponentId && nextOpponentId !== opponentIdRef.current) {
                        opponentIdRef.current = nextOpponentId;
                        const profile = await SupabaseService.getPublicProfile(nextOpponentId);
                        setOpponentProfile(profile);
                    } else if (!nextOpponentId) {
                        opponentIdRef.current = undefined;
                        setOpponentProfile(null);
                    }

                    // Check Start Condition
                    if (newWar.status === 'ACTIVE') {
                        navigate(`/war-room/${warId}`);
                        return;
                    }

                    // Auto-Initiate if Host and All Ready
                    const isHost = newWar.player_id === user.id;
                    const hostReady = (newWar.settings as any)?.host_ready;
                    const guestReady = newWar.opponent_status === 'ready';

                    if (isHost && hostReady && guestReady) {
                        console.log("Lobby: Both Commanders Ready. Auto-Initiating Assault...");

                        let hostSide: 'white' | 'black' = 'white';
                        if (newWar.battles_fought > 0 && newWar.last_winner) {
                            hostSide = newWar.last_winner === 'player' ? 'white' : 'black';
                        }

                        const battleSettings = {
                            side: hostSide,
                            opponent_side: hostSide === 'white' ? 'black' : 'white',
                            timeControl: (newWar.settings as any)?.timeControl || 'unlimited',
                            opponent_type: 'player',
                            opponent_id: newWar.opponent_id!,
                            host_formation: (newWar.settings as any)?.host_formation || [],
                            opponent_formation: (newWar.settings as any)?.opponent_formation || []
                        };

                        await SupabaseService.createBattle(newWar.id, battleSettings as any);
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'profiles' },
                async (payload: any) => {
                    const updatedProfile = payload.new as Profile;
                    // Check if this update belongs to our opponent
                    if (opponentIdRef.current && updatedProfile.id === opponentIdRef.current) {
                        console.log("Lobby: [RT] Opponent Profile Update", updatedProfile);
                        setOpponentProfile(updatedProfile);
                    }
                }
            )
            .subscribe(async (status: string, err: any) => {
                if (status === 'SUBSCRIBED') {
                    console.log("Lobby: Connected to Realtime Mesh.");
                    await channel.track({
                        user_id: user.id,
                        online_at: new Date().toISOString()
                    });
                }
                if (status === 'CHANNEL_ERROR') console.error("Lobby: Realtime Error", err);
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [warId, navigate, user]);

    // Handlers
    const handleCopyLink = () => {
        const url = `${window.location.origin}/lobby/${warId}`;
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleToggleReady = async () => {
        console.log("Lobby: handleToggleReady clicked");
        if (!war || !effectiveUser) {
            console.error("Lobby: Missing war or user");
            return;
        }

        // Profile Safety Check
        if (!myProfile) {
            console.error("Lobby: Profile not loaded yet.");
            return;
        }

        // Name Check
        const defaultName = effectiveUser?.email?.split('@')[0];
        if (myProfile.username === defaultName || myProfile.username === 'Commander' || myProfile.username === 'Guest Commander' || myProfile.username?.startsWith("Commander-")) {
            setIsRenaming(true);
            return;
        }

        console.log("Lobby: Toggling ready state. Current:", iAmReady, "Target:", !iAmReady, "isHost:", isHost);
        const result = await SupabaseService.setLobbyReady(war.id, isHost, !iAmReady);

        if (result?.error) {
            console.error("Lobby: setLobbyReady returned error", result.error);
            error("Failed to update status. Connection unstable or access denied.");
        } else {
            console.log("Lobby: Request sent successfully");
        }
    };

    const handleUpdateName = async () => {
        if (!newName.trim()) return;
        try {
            await SupabaseService.updateProfile({ username: newName });
            const updated = await SupabaseService.getProfile();
            setMyProfile(updated);
            setIsRenaming(false);
        } catch (err: any) {
            console.error("Failed to update name", err);
            if (err.message === "Not authenticated") {
                if (myProfile) {
                   setMyProfile({ ...myProfile, username: newName });
                }
                setIsRenaming(false);
            } else {
                error(err.message || "Callsign already taken. Choose another.");
            }
        }
    };

    const handleDeploy = async () => {
        if (!war) return;

        // Trust the Realtime State (via Refs for safety)
        let currentOpponentId = opponentIdRef.current || (isHost ? war.opponent_id : war.player_id);
        const currentSettings = war.settings as BattleSettings;
        const currentHostReady = !!currentSettings.host_ready;
        const currentGuestReady = war.opponent_status === 'ready';
        const currentBothReady = currentHostReady && currentGuestReady;

        // RECOVERY: If both ready but ID missing, try fetch.
        if (currentBothReady && !currentOpponentId && isHost) {
            console.log("Lobby: Detected Ready State but Missing Opponent ID. Refetching...");
            const fresh = await SupabaseService.getWarById(war.id);
            if (fresh && fresh.opponent_id) {
                setWar(fresh);
                currentOpponentId = fresh.opponent_id;
            }
        }

        // Ranked Safety Check
        if (war.is_ranked) {
            if (!currentOpponentId) {
                error("Ranked matches require a human opponent.");
                return;
            }
            if (!currentBothReady) {
                return; // Wait for both
            }
        }

        // Side Determination Logic (PvP)
        // If PvP, Host is White (or based on last_winner if rematch).
        let hostSide: 'white' | 'black' = 'white';
        if (war.battles_fought > 0 && war.last_winner) {
            hostSide = war.last_winner === 'player' ? 'white' : 'black';
        }

        // If PvP and opponent is ready -> Go
        if (currentOpponentId && currentBothReady) {
            // Initiate Battle through Service (Service creates the battle record)
            const battleSettings = {
                side: hostSide,
                opponent_side: hostSide === 'white' ? 'black' : 'white',
                timeControl: (war.settings?.timeControl || 'unlimited') as any,
                opponent_type: 'player' as const,
                opponent_id: currentOpponentId,
                host_formation: war.settings?.host_formation || [],
                opponent_formation: war.settings?.opponent_formation || []
            };
            await SupabaseService.createBattle(war.id, battleSettings);
            // Navigation handled by Realtime listener
            return;
        }

        const uplinkDetected = !!currentOpponentId || presenceCount > 1;

        // If AI or No Opponent (Convert to AI)
        if (!currentOpponentId || currentSettings.opponent_type === 'ai') {
            if (war.is_ranked) return; // Double check

            if (currentSettings.opponent_type === 'ai') {
                // Already AI, just go (difficulty set in drafting)
                // Use default AI settings (User is White)
                const battleSettings = {
                    side: 'white' as any,
                    opponent_side: 'black' as any,
                    timeControl: (war.settings?.timeControl || 'unlimited') as any,
                    opponent_type: 'ai' as const
                };
                await SupabaseService.createBattle(war.id, battleSettings);
                navigate(`/war-room/${war.id}`);
            } else if (!uplinkDetected) {
                // Was Player, but forcing start -> asking for difficulty
                // This triggers the AI Modal
                setIsAIModalOpen(true);
            }
        }
    };

    const confirmAIStart = async () => {
        if (!war) return;

        const aiSettings = {
            ...settings,
            opponent_type: 'ai' as const,
            side: 'white' as const,
            opponent_side: 'black' as const,
            timeControl: settings.timeControl || 'unlimited',
            difficulty: selectedAIDifficulty // Ensure difficulty is passed if supported or via war? 
            // createBattle settings usually expects difficulty? No, difficulty is on War.
            // But 'settings' might need it.
        };

        // Ensure war difficulty is updated if changed in modal?
        // createBattle doesn't update war difficulty column usually.
        // But we want to start.
        // Let's just create battle.

        await SupabaseService.createBattle(war.id, aiSettings as any);
        navigate(`/war-room/${war.id}`);
    };

    if (!war || !effectiveUser) {
        return (
            <div className="min-h-screen bg-tudor-black flex items-center justify-center text-tudor-gold">
                <div className="animate-pulse tracking-widest uppercase"> establishing uplink...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-tudor-black p-6 flex flex-col items-center justify-center text-center font-serif">
            {/* DOSSIER */}
            {profileDossierTarget && (
                <ProfileDossier
                    isOpen={true}
                    profile={profileDossierTarget}
                    onClose={() => setProfileDossierTarget(null)}
                    onUpgrade={() => {
                        error("Upgrade restricted to Barracks or Treasury.");
                    }}
                />
            )}

            {/* CHOICE MODAL */}
            {isChoiceModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="w-full max-w-md bg-zinc-900 border border-tudor-gold/30 p-8 rounded-sm relative shadow-2xl text-center">
                        <Sword className="w-12 h-12 text-tudor-gold mx-auto mb-4" />
                        <h2 className="text-xl font-serif text-white mb-6">Opponent Status Negative</h2>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => {
                                    setIsChoiceModalOpen(false);
                                    if (!opponentUplinkDetected) {
                                        setIsAIModalOpen(true);
                                    }
                                }}
                                disabled={war?.is_ranked}
                                className={`w-full p-4 border border-tudor-gold/50 hover:bg-tudor-gold hover:text-black transition-colors uppercase tracking-widest text-sm font-bold ${war?.is_ranked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {war?.is_ranked ? "AI Disabled (Ranked)" : "Deploy vs AI"}
                            </button>
                            <button
                                onClick={async () => {
                                    setIsChoiceModalOpen(false);
                                    await handleToggleReady();
                                }}
                                className="w-full p-4 border border-zinc-700 hover:border-white text-zinc-400 hover:text-white transition-colors uppercase tracking-widest text-sm"
                            >
                                Wait for Player
                            </button>
                        </div>
                        <button onClick={() => setIsChoiceModalOpen(false)} className="mt-6 text-xs text-zinc-600 hover:text-zinc-400 underline">Cancel</button>
                    </div>
                </div>
            )}

            {/* AI MODAL */}
            {isAIModalOpen && isHost && !opponentUplinkDetected && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="w-full max-w-md bg-zinc-900 border border-tudor-gold/30 p-8 rounded-sm relative shadow-2xl text-center">
                        <h2 className="text-2xl font-serif text-tudor-gold mb-4">Commanding AI</h2>
                        <p className="text-zinc-400 mb-6 text-sm">No opponent found. Select simulation difficulty to commence immediate battle.</p>

                        <div className="flex gap-2 justify-center mb-8">
                            {(['easy', 'medium', 'hard'] as const).map(d => (
                                <button
                                    key={d}
                                    onClick={() => setSelectedAIDifficulty(d)}
                                    className={`px-4 py-2 border uppercase tracking-wider text-xs transition-colors ${selectedAIDifficulty === d ? 'bg-tudor-gold text-black border-tudor-gold' : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'}`}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>

                        <div className="flex gap-4">
                            <button onClick={() => setIsAIModalOpen(false)} className="flex-1 py-3 border border-zinc-700 text-zinc-500 uppercase tracking-widest text-xs hover:text-white">Cancel</button>
                            <button onClick={confirmAIStart} className="flex-1 py-3 bg-tudor-gold text-black uppercase tracking-widest text-xs font-bold hover:bg-white">Engage</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-3xl w-full border border-tudor-gold/30 p-8 rounded-sm bg-black/50 backdrop-blur relative overflow-hidden shadow-2xl">

                {/* Header */}
                <div className="mb-8">
                    <Shield className="w-12 h-12 text-tudor-gold mx-auto mb-4 opacity-80" />
                    <h1 className="text-2xl md:text-3xl text-tudor-gold tracking-[0.2em] uppercase mb-2">
                        Deployment Phase
                    </h1>
                    <div className="flex items-center justify-center gap-2 text-xs text-tudor-gray font-mono uppercase tracking-wider">
                        <Wifi className={`w-3 h-3 ${(opponentUplinkDetected || settings.opponent_type === 'ai') ? 'text-green-500' : 'text-yellow-500 animate-pulse'}`} />
                        <span>
                            {opponentUplinkDetected ? "Uplink Established" :
                                settings.opponent_type === 'ai' ? "Automated Opponent Online" :
                                    "Scanning for Opponent..."}
                        </span>
                    </div>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">

                    {/* MY STATUS */}
                    <div
                        onClick={() => myProfile && setProfileDossierTarget(myProfile)}
                        className="bg-white/5 border border-tudor-gold/20 p-6 rounded flex flex-col items-center relative overflow-hidden group cursor-pointer hover:bg-white/10 transition-colors"
                    >
                        <p className="text-xs text-tudor-gray uppercase tracking-widest mb-2 flex items-center gap-2">You <Shield size={10} /></p>

                        {isRenaming ? (
                            <div className="w-full animate-fade-in" onClick={e => e.stopPropagation()}>
                                <label className="text-[10px] text-tudor-red uppercase font-bold mb-1 block">Identification Required</label>
                                <div className="flex gap-2">
                                    <input
                                        autoFocus
                                        type="text"
                                        placeholder="Enter Call Sign"
                                        className="bg-black/50 border border-tudor-gold/50 text-tudor-gold p-2 text-sm w-full outline-none font-mono"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                                    />
                                    <button onClick={handleUpdateName} className="bg-tudor-gold text-black px-3 font-bold text-xs uppercase hover:bg-white">Set</button>
                                </div>
                            </div>
                        ) : (
                            <h2 className="text-xl text-white font-mono mb-4 group-hover:text-tudor-gold transition-colors">{myProfile?.username}</h2>
                        )}

                        <div className={`mt-auto px-4 py-1 rounded text-xs border uppercase tracking-widest font-bold ${iAmReady ? 'bg-green-900/40 border-green-500 text-green-400' : 'bg-yellow-900/20 border-yellow-700 text-yellow-500'}`}>
                            {iAmReady ? "Ready" : "Standby"}
                        </div>
                    </div>

                    {/* OPPONENT STATUS */}
                    <div
                        onClick={() => opponentProfile && setProfileDossierTarget(opponentProfile)}
                        className={`bg-white/5 border border-red-900/30 p-6 rounded flex flex-col items-center relative transition-colors ${opponentProfile ? 'cursor-pointer hover:bg-white/10' : ''}`}
                    >
                        <p className="text-xs text-red-400/60 uppercase tracking-widest mb-2 flex items-center gap-2">Opponent <Swords size={10} /></p>

                        {opponentId ? (
                            <>
                                <h2 className="text-xl text-red-200 font-mono mb-4 group-hover:text-tudor-gold transition-colors">{opponentProfile?.username || "Unknown Commander"}</h2>
                                <div className={`mt-auto px-4 py-1 rounded text-xs border uppercase tracking-widest font-bold ${opponentReady ? 'bg-green-900/40 border-green-500 text-green-400' : 'bg-red-900/10 border-red-900 text-red-700'}`}>
                                    {opponentReady ? "Ready" : "Preparing"}
                                </div>
                            </>
                        ) : settings.opponent_type === 'ai' ? (
                            <>
                                <h2 className="text-xl text-red-200 font-mono mb-4">Tactical AI</h2>
                                <div className="mt-auto px-4 py-1 rounded text-xs border border-green-500 bg-green-900/40 text-green-400 uppercase tracking-widest font-bold">
                                    Online
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full gap-2 opacity-50">
                                <div className="animate-spin w-6 h-6 border-2 border-tudor-gray border-t-transparent rounded-full"></div>
                                <span className="text-xs font-mono text-tudor-gray">Waiting for connection...</span>
                            </div>
                        )}
                    </div>
                </div>


                {/* Link Sharing (Host Only - ONLY IF PVP) */}
                {!opponentId && isHost && settings.opponent_type !== 'ai' && (
                    <div className="mb-8 animate-fade-in bg-tudor-gold/5 p-4 rounded border border-dashed border-tudor-gold/30">
                        <p className="text-xs text-tudor-gold mb-2 uppercase tracking-wider">Share Uplink Coordinates</p>
                        <button
                            onClick={handleCopyLink}
                            className="flex items-center gap-2 mx-auto px-6 py-2 bg-black border border-tudor-gold/50 text-tudor-gold hover:bg-tudor-gold hover:text-black transition-all text-xs font-mono uppercase"
                        >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? "Link Copied" : "Copy Secure Link"}
                        </button>
                    </div>
                )}

                {/* Actions */}
                {/* Actions */}
                <div className="space-y-4 relative">
                    {/* Tooltip for Opponent Status */}
                    {opponentId && (
                        <div className="text-center text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 h-4">
                            {opponentReady ? <span className="text-green-500">Opponent Ready</span> : "Waiting on Opponent..."}
                        </div>
                    )}

                    <button
                        onClick={() => {
                            // 0. Draft Check
                            if (needsDrafting) {
                                navigate(`/drafting?warId=${warId}&role=guest`);
                                return;
                            }

                            // 1. If AI -> Start
                            if (settings.opponent_type === 'ai') {
                                handleDeploy();
                                return;
                            }
                            // 2. If PvP and Both Ready -> Await Auto-Start
                            if (bothReady) {
                                return;
                            }
                            // 3. If PvP and Not Both Ready -> Toggle
                            const opponentDetected = opponentUplinkDetected || war?.opponent_status === 'connected' || war?.opponent_status === 'ready';

                            if (opponentDetected) {
                                handleToggleReady();
                                return;
                            }
                            // 4. If No Opponent (Waiting)
                            // This was the bug: If opponent_type is 'player' (default), but opponentId is null, 
                            // we must NOT fall through to execution if we are just marking ready.
                            if (!opponentDetected) {
                                // If it IS an AI game, handling above covers it.
                                // So here we assume it's intended to be Human vs Human but no one joined yet.

                                if (iAmReady) {
                                    handleToggleReady(); // Cancel
                                } else {
                                    // If Ranked, we can't switch to AI anyway.
                                    // If Normal, prompt user.
                                    if (war?.is_ranked) {
                                        handleToggleReady(); // Just mark "Deployed" (Ready) waiting for opponent
                                        // Or show alert? No, marking ready is fine.
                                    } else {
                                        setIsChoiceModalOpen(true); // Prompt: Wait or AI?
                                    }
                                }
                            }
                        }}
                        disabled={isRenaming || !myProfile || bothReady}
                        className={`w-full py-4 px-8 rounded text-center text-[18px] font-mono text-xl uppercase tracking-[0.3em] transition-all flex items-center justify-center gap-3 shadow-lg 
                            ${(iAmReady || bothReady)
                                ? 'bg-transparent border-2 border-tudor-gold text-tudor-gold'
                                : 'bg-tudor-gold text-black border-2 border-tudor-gold hover:bg-white'
                            } 
                            ${(isRenaming || bothReady) ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                    >
                        {needsDrafting ? (
                            "Assemble Army"
                        ) : bothReady ? (
                            "DEPLOYED"
                        ) : iAmReady ? (
                            "WAITING..."
                        ) : (
                            "Deploy to Battle"
                        )}
                    </button>

                    {bothReady && (
                        <p className="text-center text-xs text-tudor-gold animate-pulse mt-2">Awaiting Battle Uplink...</p>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Lobby;
